import type { CSSProperties } from 'react';
import type { ThemeCaptionStyle } from 'fym-shared/src/music24/releaseThemes';

/**
 * The shapes of the drawn frames (design s, the logic class: `bars`, `particles`, `lineClip`,
 * `words`). Pure: the same seed gives the same picture, so a frame does not move between renders.
 */

/** One drawn piece (a bar, a dot) with a stable key. */
export interface FramePiece {
  key: string;
  style: CSSProperties;
}

/** The heights of `count` waveform bars, 25 % to 100 %. */
export function frameBars(count: number, seed: number): FramePiece[] {
  const bars: FramePiece[] = [];
  for (let index = 0; index < count; index += 1) {
    const value = 0.25 + 0.75 * Math.abs(Math.sin(index * 0.9 + seed) * Math.cos(index * 0.37 + seed * 2));
    bars.push({ key: `bar-${index}`, style: { height: `${Math.round(value * 100)}%` } });
  }
  return bars;
}

/** The waveform line as a clip path: a band of 7 % of the height that follows the curve. */
export function frameLineClip(seed: number): CSSProperties {
  const top: string[] = [];
  const bottom: string[] = [];
  for (let index = 0; index <= 60; index += 1) {
    const y =
      46 + 40 * Math.sin(index * 0.55 + seed) * Math.cos(index * 0.18) * (0.4 + 0.6 * Math.abs(Math.sin(index * 0.11)));
    const x = `${((index * 100) / 60).toFixed(1)}%`;
    top.push(`${x} ${y.toFixed(1)}%`);
    bottom.unshift(`${x} ${(y + 7).toFixed(1)}%`);
  }
  return { clipPath: `polygon(${top.concat(bottom).join(', ')})` };
}

/** The state of one word of the sung line: lit, the word sung now, or still to come. */
export type FrameWordState = 'lit' | 'now' | 'off';

export interface FrameWord {
  key: string;
  text: string;
  state: FrameWordState;
}

/**
 * The words of the sung line in the caption style of the theme: `line` shows the whole line lit;
 * `word` and `kinetic` light the words up to `lit` and dim the rest.
 */
export function frameWords(text: string, lit: number, caption: ThemeCaptionStyle): FrameWord[] {
  return text
    .split(/\s+/)
    .filter((word) => word !== '')
    .map((word, index) => {
      let state: FrameWordState = 'lit';
      if (caption !== 'line') {
        state = index < lit ? 'lit' : index === lit ? 'now' : 'off';
      }
      return { key: `${index}-${word}`, text: word, state };
    });
}

/** A seed from a text, so each song gets its own bars. */
export function frameSeed(text: string): number {
  return Array.from(text).length % 13;
}
